import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonCore } from '@components/button-core';
import { IconCheckCircle, IconCritical, IconCross, IconWarning } from '@components/icons';
import { ModalV2 } from '@components/modals/modal-v2/src/modal-v2';
import { SM_VIEWPORT } from '@constants/viewport';
import { useWindowSize } from '@hooks/use-window-size';
import classNames from 'classnames';

import { ConfirmModalV2Props } from './confirm-modal-v2.types';

// Button variant mapping by modal type
const BUTTON_VARIANT_MAP = {
  critical: { confirm: 'critical', secondary: 'secondary' },
  success: { confirm: 'accent-primary', secondary: 'accent-secondary' },
  warning: { confirm: 'warning', secondary: 'secondary' },
} as const;

/**
 * ConfirmModalV2 - Advanced confirmation modal with support for multiple types
 *
 * Features:
 * - Multiple dialog types (critical, success, warning, default)
 * - Optional alert box with detailed information
 * - Responsive layout (mobile: stacked buttons, desktop: horizontal)
 * - Customizable icons and styling
 * - Built on ModalV2 for consistency
 *
 * @example
 * ```tsx
 * <ConfirmModalV2
 *   isOpen={isOpen}
 *   type="critical"
 *   title="Delete ticket group?"
 *   description="This action cannot be undone."
 *   confirmText="Yes, delete"
 *   cancelText="Cancel"
 *   onConfirm={handleDelete}
 *   onCancel={() => setIsOpen(false)}
 * />
 * ```
 */
export const ConfirmModalV2 = ({
  isOpen,
  onConfirm,
  onCancel,
  type,
  title,
  description,
  icon,
  alertContent,
  confirmMessage,
  isConfirmDisabled = false,
  confirmText,
  cancelText,
  layout = 'centered',
  titleClassName = 'text-headline-m sm:text-headline-l',
  descriptionClassName = 'text-body-s',
  contentClassName,
  surfaceClassName,
  zIndex,
}: ConfirmModalV2Props) => {
  const { t } = useTranslation();
  const isButtonWullWidth = useWindowSize(SM_VIEWPORT);

  // Determine icon based on type if not provided
  const iconLarge = useMemo(() => {
    if (icon) return icon;

    const iconMap = {
      critical: <IconCritical className='w-10 h-10 text-critical-no-bg' />,
      success: <IconCheckCircle className='w-10 h-10 text-success-no-bg' />,
      warning: <IconWarning className='w-10 h-10 text-warning-no-bg' />,
    };

    return iconMap[type];
  }, [icon, type]);

  const iconSmall = useMemo(() => {
    const iconMap = {
      critical: <IconCritical className='w-5 h-5 text-critical' />,
      success: <IconCheckCircle className='w-5 h-5 text-success' />,
      warning: <IconWarning className='w-5 h-5 text-warning' />,
    };

    return iconMap[type];
  }, [type]);

  const buttonClass = 'w-full md:w-fit';

  // Unified button renderer
  const getButton = (
    variant: 'critical' | 'secondary' | 'accent-primary' | 'accent-secondary' | 'warning' | 'primary',
    text: string,
    onClick: (() => void) | undefined,
    testId: string,
    disabled = false
  ) => (
    <ButtonCore
      onClick={onClick}
      data-testid={testId}
      className={buttonClass}
      appearance='filled'
      variant={variant}
      disabled={disabled}
      fullWidth={isButtonWullWidth}>
      {text}
    </ButtonCore>
  );

  const getConfirmButton = () =>
    getButton(
      BUTTON_VARIANT_MAP[type].confirm,
      confirmText ?? t('common:yes'),
      onConfirm,
      'confirm-modal-v2__confirm-button',
      isConfirmDisabled
    );

  const getSecondaryButton = () =>
    getButton(
      BUTTON_VARIANT_MAP[type].secondary,
      cancelText ?? t('common:no'),
      onCancel,
      'confirm-modal-v2__secondary-button'
    );

  // Description can be string or array of strings
  const renderDescription = () => {
    if (!description) return null;

    if (Array.isArray(description)) {
      return (
        <div className={classNames('text-center space-y-1', descriptionClassName)}>
          {description.map((line) => (
            <p key={line} className='text-secondary text-body-s'>
              {line}
            </p>
          ))}
        </div>
      );
    }

    return <p className={classNames('text-center text-secondary', descriptionClassName)}>{description}</p>;
  };

  // Alert box with optional content
  const renderAlertBox = () => {
    if (!alertContent) return null;

    const bgMap = {
      critical: { background: 'bg-critical', iconColor: 'text-critical-no-bg', textColor: 'text-critical' },
      success: { background: 'bg-success', iconColor: 'text-success-no-bg', textColor: 'text-success' },
      warning: { background: 'bg-warning', iconColor: 'text-warning-no-bg', textColor: 'text-warning' },
    };

    return (
      <div
        className={classNames('w-full p-4 rounded-lg flex gap-2', bgMap[type].background)}
        data-testid='confirm-modal-v2__alert-box'>
        <span className={bgMap[type].iconColor}>{iconSmall}</span>
        <div className={classNames('text-body-s flex-1', bgMap[type].textColor)}>{alertContent}</div>
      </div>
    );
  };

  // Determine if footer should be sticky (only if there's content that needs scrolling)
  const shouldBeSticky = !!alertContent;

  // Figma "Modal V2" dialog (nodes 20138-335810 / 20138-336018): left-aligned header with a close
  // icon, bordered footer. Buttons are right-aligned and intrinsic on desktop, split 50/50 on mobile.
  // A floating centered dialog at every breakpoint — never a bottom sheet.
  if (layout === 'dialog') {
    const descriptionLines = description === undefined || Array.isArray(description) ? description : [description];
    return (
      <ModalV2
        isOpen={isOpen}
        onClose={onCancel ?? (() => {})}
        surfaceClassName={surfaceClassName}
        zIndex={zIndex}
        centerOnMobile
        hasMobileHandle={false}
        enableDragToClose={false}
        borderRadiusClass='rounded-lg'>
        <div className={classNames('flex flex-col', contentClassName)}>
          <div className='flex w-full items-start justify-between gap-2.5 px-8 py-6'>
            <div className='flex min-w-0 flex-1 flex-col gap-2'>
              <h2 className='text-headline-m text-primary'>{title}</h2>
              {descriptionLines?.map((line) => (
                <p key={line} className='text-body-m text-secondary'>
                  {line}
                </p>
              ))}
            </div>
            <button
              type='button'
              data-testid='confirm-modal-v2__close-button'
              className='shrink-0 p-1 -m-1 hover:bg-transparent-10 rounded-lg transition-colors'
              onClick={onCancel ?? (() => {})}
              aria-label='Close modal'>
              <IconCross className='text-primary w-6 h-6' />
            </button>
          </div>

          <div className='flex w-full items-center justify-end gap-2 border-t border-extra-soft p-4 sm:px-8 sm:py-6'>
            <ButtonCore
              appearance='filled'
              variant='secondary'
              className='flex-1 sm:flex-none'
              data-testid='confirm-modal-v2__secondary-button'
              onClick={onCancel}>
              {cancelText ?? t('common:no')}
            </ButtonCore>
            <ButtonCore
              appearance='filled'
              variant='primary'
              className='flex-1 sm:flex-none'
              data-testid='confirm-modal-v2__confirm-button'
              onClick={onConfirm}
              disabled={isConfirmDisabled}>
              {confirmText ?? t('common:yes')}
            </ButtonCore>
          </div>
        </div>
      </ModalV2>
    );
  }

  return (
    <ModalV2 isOpen={isOpen} onClose={onCancel ?? (() => {})} surfaceClassName={surfaceClassName} zIndex={zIndex}>
      {/* Content section */}
      <div
        className={classNames(
          'flex flex-col gap-6 sm:gap-8 items-center pt-10 px-6 sm:px-20 sm:pt-12 relative',
          { 'pb-6': shouldBeSticky, 'pb-10 sm:pb-12': !shouldBeSticky },
          contentClassName
        )}>
        {/* Close button */}
        <div className='absolute top-4 right-4 sm:top-6 sm:right-6'>
          <button
            data-testid='confirm-modal-v2__close-button'
            className='flex justify-center items-center p-1 hover:bg-transparent-10 rounded-lg transition-colors'
            onClick={onCancel ?? (() => {})}
            aria-label='Close modal'>
            <IconCross className='text-primary w-6 h-6' />
          </button>
        </div>

        {/* Header section: icon + title + description */}
        <div className='w-full flex flex-col gap-3 items-center '>
          {/* Icon */}
          {iconLarge && <div className='flex justify-center'>{iconLarge}</div>}

          {/* Title */}
          <h2 className={classNames('text-center text-primary text-headline-m sm:text-headline-l', titleClassName)}>
            {title}
          </h2>

          {/* Description */}
          {renderDescription()}
        </div>
        {(!!alertContent || !!confirmMessage) && (
          <div className='w-full flex flex-col gap-4 sm:gap-6 items-center '>
            {/* Alert box (optional) */}
            {renderAlertBox()}

            {/* Confirm message (optional) */}
            {confirmMessage && <div className='w-full text-body-s text-secondary text-left'>{confirmMessage}</div>}
          </div>
        )}

        {/* Action buttons - only inline if not sticky */}
        {!shouldBeSticky && (
          <div className='w-full flex gap-3 items-center justify-center sm:gap-2 flex-col sm:flex-row-reverse'>
            {getConfirmButton()}
            {getSecondaryButton()}
          </div>
        )}
      </div>

      {/* Sticky footer with buttons - only when there's scrollable content */}
      {shouldBeSticky && (
        <footer
          className={classNames(
            'sticky bottom-0 w-full z-20 bg-primary border-t border-extra-soft',
            'p-4 pb-8 sm:px-8 sm:py-6'
          )}>
          <div className='flex gap-3 items-center justify-center sm:gap-2 flex-col sm:flex-row-reverse w-full'>
            {getConfirmButton()}
            {getSecondaryButton()}
          </div>
        </footer>
      )}
    </ModalV2>
  );
};
